import React, { useMemo, useState, useEffect } from 'react';
import { getRoles, updateRolePermissions, getModules, getRolePermissions } from './api/roleApi';
import CapabilitiesManagement from './components/CapabilitiesManagement';
import ModulesManagement from './components/ModulesManagement';
import './SetPermission.css';

const PERMISSION_NAV_GROUPS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    description: 'Workspace overview and executive dashboards.',
    modules: ['crm-dashboard', 'audit-dashboard', 'pm-dashboard', 'dashboard']
  },
  {
    key: 'crm',
    label: 'CRM',
    description: 'Prospect, leads, clients, commercial project, and sales workspaces.',
    modules: ['prospect', 'leads', 'proposal', 'client', 'project', 'task', 'calendar', 'analysis', 'sales-report', 'achievement']
  },
  {
    key: 'audit-platform',
    label: 'Audit Platform',
    description: 'Audit process, audit clients, templates, libraries, and reconciliation.',
    modules: ['audit', 'audit-output-template', 'audit-libraries', 'audit-dashboard', 'audit-form-catalog', 'audit-reconciliation']
  },
  {
    key: 'project-management',
    label: 'Project Management',
    description: 'PM projects, timesheet, and library.',
    modules: ['pm-projects', 'pm-timesheet', 'pm-library']
  },
  {
    key: 'finance',
    label: 'Finance',
    description: 'Invoices, payments, expenses, targets, and finance master data.',
    modules: ['invoice', 'payment', 'expense', 'sales-target', 'chart-of-account', 'master-currency', 'currency', 'expense-category']
  },
  {
    key: 'hris',
    label: 'HRIS',
    description: 'Employees, department, attendance, payroll, leave, and HR operations.',
    modules: ['hris', 'department', 'employee', 'leave', 'payroll', 'attendance', 'performance', 'recruitment']
  },
  {
    key: 'assets-knowledge',
    label: 'Assets & Knowledge',
    description: 'Asset, academy, knowledge, and document management.',
    modules: ['asset', 'asset-management', 'academy', 'knowledge-management', 'document-management']
  },
  {
    key: 'settings',
    label: 'Settings',
    description: 'Roles, permissions, templates, workflows, and system configuration.',
    modules: ['create-role', 'set-permission', 'workflow', 'create-template', 'rate-management', 'create-type-unit', 'proposal-stage', 'format-number', 'email-template', 'email-configuration', 'api-key']
  }
];

const fallbackGroup = {
  key: 'other',
  label: 'Other Modules',
  description: 'Modules not yet mapped to a sidebar group.',
  modules: []
};

// Legacy module keys superseded by a split into dedicated modules — hidden from
// /set-permission entirely even if the row still exists in the DB (see backend
// migration V20260722_001__split_project_management_module.sql).
const DEPRECATED_MODULE_KEYS = ['project-management'];

const normalizeKey = (value) => (value || '').toString().trim().toLowerCase();

const findPermissionGroup = (feature) => {
  const featureKey = normalizeKey(feature.key);
  const featureName = normalizeKey(feature.name);

  const exactGroup = PERMISSION_NAV_GROUPS.find((group) =>
    group.modules.some((moduleKey) => featureKey === normalizeKey(moduleKey))
  );

  if (exactGroup) return exactGroup;

  return PERMISSION_NAV_GROUPS.find((group) =>
    group.modules.some((moduleKey) => {
      const normalizedModuleKey = normalizeKey(moduleKey);
      return featureName.includes(normalizedModuleKey);
    })
  ) || fallbackGroup;
};

const groupFeaturesByNavigation = (features = []) => {
  const buckets = new Map(
    [...PERMISSION_NAV_GROUPS, fallbackGroup].map((group) => [
      group.key,
      { ...group, features: [] }
    ])
  );

  features.forEach((feature) => {
    const group = findPermissionGroup(feature);
    buckets.get(group.key).features.push(feature);
  });

  return Array.from(buckets.values()).filter((group) => group.features.length > 0);
};

const groupHasEnabledPermission = (group, permissions = {}) =>
  group.features.some((feature) =>
    Object.values(permissions[feature.key] || {}).some(Boolean)
  );

const SetPermission = () => {
  const [activeTab, setActiveTab] = useState('permissions');
  const [selectedRole, setSelectedRole] = useState('');
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [dirtyModules, setDirtyModules] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [features, setFeatures] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [permissionsVersion, setPermissionsVersion] = useState(0);


  useEffect(() => {
    loadRoles();
    loadFeatures();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      loadRolePermissions(selectedRole);
    }
  }, [selectedRole]);

  const groupedFeatures = useMemo(() => groupFeaturesByNavigation(features), [features]);

  useEffect(() => {
    if (!selectedRole || groupedFeatures.length === 0) {
      setOpenGroups({});
      return;
    }

    const nextOpenGroups = {};
    let hasOpenGroup = false;

    groupedFeatures.forEach((group) => {
      const shouldOpen = groupHasEnabledPermission(group, permissions);
      nextOpenGroups[group.key] = shouldOpen;
      if (shouldOpen) hasOpenGroup = true;
    });

    if (!hasOpenGroup && groupedFeatures[0]) {
      nextOpenGroups[groupedFeatures[0].key] = true;
    }

    setOpenGroups(nextOpenGroups);
  }, [selectedRole, groupedFeatures, permissionsVersion]);

  const loadRoles = async () => {
    setIsLoading(true);
    try {
      console.log('=== DEBUG: Loading Roles ===');
      console.log('Calling getRoles()...');
      const response = await getRoles();
      console.log('Roles response:', response);
      console.log('Roles type:', typeof response);
      console.log('Roles length:', Array.isArray(response) ? response.length : 'Not an array');
      setRoles(response);
      console.log('Roles state updated');
      console.log('=== END DEBUG ===');
    } catch (error) {
      console.error('=== DEBUG: Error Loading Roles ===');
      console.error('Error:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('=== END DEBUG ===');
      setRoles([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFeatures = async () => {
    setIsLoading(true);
    try {
      const data = await getModules();
      const visible = (data || []).filter(
        (m) => !DEPRECATED_MODULE_KEYS.includes(normalizeKey(m.key || m.moduleKey))
      );
      const normalized = visible.map(m => ({
        name: m.name || m.moduleName,
        key: m.key || m.moduleKey,
        capabilities: (m.capabilities || []).map(c => ({
          key: (c.key || c.capabilityKey || '').toString().trim(),
          label: c.label || c.capabilityName,
          description: c.description || ''
        }))
      }));
      // Deduplicate modules by key and merge/dedup their capabilities
      const byKey = new Map();
      for (const mod of normalized) {
        const key = (mod.key || '').toString().trim();
        if (!key) continue;
        const existing = byKey.get(key);
        if (!existing) {
          byKey.set(key, { name: mod.name, key, capabilities: [...mod.capabilities] });
        } else {
          existing.name = existing.name || mod.name;
          existing.capabilities.push(...mod.capabilities);
        }
      }
      const merged = Array.from(byKey.values()).map(mod => {
        const seen = new Set();
        const caps = [];
        for (const cap of mod.capabilities) {
          if (!seen.has(cap.key)) {
            seen.add(cap.key);
            caps.push(cap);
          }
        }
        return { ...mod, capabilities: caps };
      });
      setFeatures(merged);
    } catch (error) {
      console.error('Error loading features/modules:', error);
      setFeatures([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRolePermissions = async (roleId) => {
    try {
      console.log('=== DEBUG: Loading Role Permissions ===');
      console.log('Role ID:', roleId);
      const data = await getRolePermissions(roleId);
      console.log('Loaded Permissions Data:', data);
      setPermissions(data || {});
      setDirtyModules({});
      setPermissionsVersion(prev => prev + 1);
      console.log('Permissions state updated');
      console.log('=== END DEBUG ===');
    } catch (error) {
      console.error('Error loading role permissions:', error);
      setPermissions({});
    }
  };

  const handlePermissionChange = (featureKey, capabilityKey, value) => {
    console.log('=== DEBUG: Permission Change ===');
    console.log('Feature Key:', featureKey);
    console.log('Capability Key:', capabilityKey);
    console.log('Value:', value);
    console.log('Current Permissions:', permissions);
    
    setPermissions(prev => {
      const newPermissions = {
        ...prev,
        [featureKey]: {
          ...prev[featureKey],
          [capabilityKey]: value
        }
      };

      // ✅ Implement mutual exclusion for view capabilities
      // Only ONE of these can be selected at a time: view_global, view_department, view_own
      if (value && (capabilityKey === 'view_own' || capabilityKey === 'view_global' || capabilityKey === 'view_department')) {
        // When enabling one view capability, disable the other two
        const viewCapabilities = ['view_global', 'view_department', 'view_own'];
        viewCapabilities.forEach(viewCap => {
          if (viewCap !== capabilityKey) {
            // Uncheck other view capabilities
            if (newPermissions[featureKey]?.[viewCap]) {
              newPermissions[featureKey][viewCap] = false;
            }
          }
        });
      }

      console.log('New Permissions:', newPermissions);
      console.log('=== END DEBUG ===');
      return newPermissions;
    });
    setDirtyModules(prev => ({
      ...prev,
      [featureKey]: true
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) {
      alert('Please select a role first');
      return;
    }

    try {
      // ✅ DEBUG: Log before saving
      console.log('=== DEBUG: Save Permissions ===');
      console.log('Selected Role ID:', selectedRole);
      console.log('Current Permissions:', permissions);
      console.log('Features:', features);
      
      // Check if features are loaded from API or fallback
      if (features.length === 0) {
        alert('No features loaded. Please check if modules and capabilities are initialized in the database.');
        return;
      }
      
      // Sanitize payload: only send modules/capabilities changed in the UI.
      // The backend updates only submitted modules, so this avoids long full-role
      // rewrites that can time out when the permission catalog is large.
      const clean = {};
      for (const feature of features) {
        if (!dirtyModules[feature.key]) continue;
        const modPerms = permissions[feature.key] || {};
        const capSet = new Set(feature.capabilities.map(c => c.key));
        clean[feature.key] = {};
        for (const capKey of capSet) {
          clean[feature.key][capKey] = Boolean(modPerms[capKey]);
        }
      }

      if (Object.keys(clean).length === 0) {
        alert('No permission changes to save.');
        return;
      }
      
      console.log('Clean Payload:', clean);
      console.log('=== END DEBUG ===');
      
      const result = await updateRolePermissions(selectedRole, clean);
      console.log('=== DEBUG: Save Result ===');
      console.log('Save Result:', result);
      console.log('=== END DEBUG ===');
      
      alert('Permissions saved successfully!');
      
      // ✅ Reload permissions after save
      console.log('Reloading permissions...');
      await loadRolePermissions(selectedRole);
      setDirtyModules({});
      console.log('Permissions reloaded');
    } catch (error) {
      console.error('Error saving permissions:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // More detailed error message
      let errorMessage = 'Error saving permissions: ';
      if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage += error.response.data.error;
      } else {
        errorMessage += error.message;
      }
      
      // Check if it's a database issue
      if (error.response?.status === 500) {
        errorMessage += '\n\nThis might be due to missing modules or capabilities in the database. Please contact administrator to initialize the permission system.';
      }
      
      alert(errorMessage);
    }
  };


  const getCapabilityStatus = (featureKey, capabilityKey) => {
    return permissions[featureKey]?.[capabilityKey] || false;
  };

  const toggleGroup = (groupKey) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  if (isLoading) {
    return (
      <div className="container-xl">
        <div className="row g-2 align-items-center">
          <div className="col">
            <h3 className="page-title m-0">Set Permissions</h3>
          </div>
        </div>
        <div className="page-body">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xl">
      <div className="row g-2 align-items-center">
        <div className="col">
          <h3 className="page-title m-0">Permission Management</h3>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          {/* Tab Navigation */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                    <li className="nav-item-tab" role="presentation">
                      <a
                        href="#tabs-permissions"
                        className={`nav-link ${activeTab === 'permissions' ? 'active' : ''}`}
                        data-bs-toggle="tab"
                        aria-selected={activeTab === 'permissions'}
                        tabIndex={activeTab === 'permissions' ? '0' : '-1'}
                        role="tab"
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab('permissions');
                        }}
                      >
                        Set Permissions
                      </a>
                    </li>
                    <li className="nav-item-tab" role="presentation">
                      <a
                        href="#tabs-capabilities"
                        className={`nav-link ${activeTab === 'capabilities' ? 'active' : ''}`}
                        data-bs-toggle="tab"
                        aria-selected={activeTab === 'capabilities'}
                        tabIndex={activeTab === 'capabilities' ? '0' : '-1'}
                        role="tab"
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab('capabilities');
                        }}
                      >
                        Manage Capabilities
                      </a>
                    </li>
                    <li className="nav-item-tab" role="presentation">
                      <a
                        href="#tabs-modules"
                        className={`nav-link ${activeTab === 'modules' ? 'active' : ''}`}
                        data-bs-toggle="tab"
                        aria-selected={activeTab === 'modules'}
                        tabIndex={activeTab === 'modules' ? '0' : '-1'}
                        role="tab"
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab('modules');
                        }}
                      >
                        Manage Modules
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="card-body">
                  <div className="tab-content">
                    {/* Set Permissions Tab */}
                    <div className={`tab-pane ${activeTab === 'permissions' ? 'active show' : ''}`} id="tabs-permissions" role="tabpanel">
                      <div className="row g-2 align-items-center mb-4">
                        <div className="col">
                          <h4 className="m-0">Set Permissions</h4>
                        </div>
                        <div className="col-auto ms-auto d-print-none">
                          <div className="btn-list">
                            <button
                              className="btn btn-primary"
                              onClick={handleSavePermissions}
                              disabled={!selectedRole}
                            >
                              Save Permissions
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Role Selector */}
                      <div className="col-md-3">
                        <div className="mb-4">
                          <label className="form-label fw-bold">Select Role</label>
                          <select
                            className="form-select"
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                          >
                            <option value="">Choose a role...</option>
                            {roles.map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.name || role.nameRole}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Permissions Table */}
                      {selectedRole && (
                        <div className="permission-groups">
                          {groupedFeatures.map((group) => (
                            <section className={`permission-group ${openGroups[group.key] ? 'permission-group--open' : ''}`} key={group.key}>
                              <button
                                type="button"
                                className="permission-group__header"
                                onClick={() => toggleGroup(group.key)}
                                aria-expanded={Boolean(openGroups[group.key])}
                              >
                                <div>
                                  <div className="permission-group__eyebrow">Navigation Group</div>
                                  <h5 className="permission-group__title">{group.label}</h5>
                                  <p className="permission-group__description">{group.description}</p>
                                </div>
                                <span className="permission-group__meta">
                                  <span className="permission-group__count">{group.features.length} modules</span>
                                  <span className="permission-group__chevron" aria-hidden="true">v</span>
                                </span>
                              </button>
                              {openGroups[group.key] && (
                                <>
                                  <div className="table-responsive">
                                    <table className="table table-vcenter permission-table">
                                      <thead>
                                        <tr>
                                          <th style={{ width: '30%' }}>Module</th>
                                          <th style={{ width: '70%' }}>Capabilities</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {group.features.map((feature) => (
                                          <tr key={feature.key}>
                                            <td>
                                              <div className="fw-bold">{feature.name}</div>
                                              <div className="text-muted small">{feature.key}</div>
                                            </td>
                                            <td>
                                              <div className="permission-capabilities">
                                                {feature.capabilities.map((capability) => (
                                                  <div key={`${feature.key}-${capability.key}`} className="form-check form-check-inline permission-capability">
                                                    <input
                                                      className="form-check-input"
                                                      type="checkbox"
                                                      id={`${feature.key}-${capability.key}`}
                                                      checked={getCapabilityStatus(feature.key, capability.key)}
                                                      onChange={(e) => handlePermissionChange(feature.key, capability.key, e.target.checked)}
                                                    />
                                                    <label
                                                      className="form-check-label"
                                                      htmlFor={`${feature.key}-${capability.key}`}
                                                      title={capability.description}
                                                    >
                                                      {capability.label}
                                                    </label>
                                                  </div>
                                                ))}
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                              )}
                            </section>
                          ))}
                        </div>
                      )}

                      {!selectedRole && (
                        <div className="text-center text-muted py-5">
                          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 12l2 2 4-4"></path>
                            <path d="M21 12c-1 0-2-.9-2-2s1-2 2-2 2 .9 2 2-1 2-2 2z"></path>
                            <path d="M3 12c1 0 2-.9 2-2s-1-2-2-2-2 .9-2 2 1 2 2 2z"></path>
                            <path d="M12 3c0 1-.9 2-2 2s-2-1-2-2 1-2 2-2 2 1 2 2z"></path>
                            <path d="M12 21c0-1 .9-2 2-2s2 1 2 2-1 2-2 2-2-1-2-2z"></path>
                          </svg>
                          <p className="mt-3">Select a role to configure permissions</p>
                        </div>
                      )}
                    </div>

                     {/* Manage Capabilities Tab */}
                     <div className={`tab-pane ${activeTab === 'capabilities' ? 'active show' : ''}`} id="tabs-capabilities" role="tabpanel">
                       <CapabilitiesManagement />
                     </div>

                     {/* Manage Modules Tab */}
                     <div className={`tab-pane ${activeTab === 'modules' ? 'active show' : ''}`} id="tabs-modules" role="tabpanel">
                       <ModulesManagement />
                     </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetPermission; 
